import { useCallback, useEffect, useMemo } from "react";
import LS from "../../utils/LocalStorage";
import { LocalStorageEnum } from "../../utils/enums";
import { ItemType } from "../../utils/types";
import {
  selectAddonGroupLookup,
  selectAddonSubItemLookup,
  selectMenuLookups,
  selectResolveMenuSubItem,
  selectSubItemLookup,
} from "../redux/selectors/menuLookupSelectors";
import { mergeMenuLookupsFromItems } from "../redux/slices/configSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

function readItemsDataForRestaurant(
  restaurantId: number | undefined
): ItemType[] {
  if (typeof window === "undefined" || !restaurantId) {
    return [];
  }

  const itemsObj = LS._get(LocalStorageEnum.ITEMS_DATA, true);
  if (!itemsObj || typeof itemsObj !== "object") {
    return [];
  }

  return Object.values(itemsObj as Record<string, ItemType>).filter(
    (item) => item?.restaurantId === restaurantId
  );
}

export function useMenuLookups() {
  const subItemLookup = useAppSelector(selectSubItemLookup);
  const addonSubItemLookup = useAppSelector(selectAddonSubItemLookup);
  const addonGroupLookup = useAppSelector(selectAddonGroupLookup);
  const menuLookups = useAppSelector(selectMenuLookups);
  const resolveMenuSubItem = useAppSelector(selectResolveMenuSubItem);

  return useMemo(
    () => ({
      subItemLookup,
      addonSubItemLookup,
      addonGroupLookup,
      menuLookups,
      resolveSubItem: (entityId: string) => subItemLookup[entityId],
      resolveAddonSubItem: (entityId: string) => addonSubItemLookup[entityId],
      resolveAddonGroup: (entityId: string) => addonGroupLookup[entityId],
      resolveMenuSubItem,
    }),
    [
      addonGroupLookup,
      addonSubItemLookup,
      menuLookups,
      resolveMenuSubItem,
      subItemLookup,
    ]
  );
}

/** Hydrates addon + enriched sub-item lookups from ITEMS_DATA local storage. */
export function useMenuLookupHydration() {
  const dispatch = useAppDispatch();
  const restaurantId = useAppSelector(
    (state) => state.config.restaurantInfo?.id
  );

  const hydrateFromLocalStorage = useCallback(() => {
    const items = readItemsDataForRestaurant(restaurantId);
    if (items.length > 0) {
      dispatch(mergeMenuLookupsFromItems(items));
    }
  }, [dispatch, restaurantId]);

  useEffect(() => {
    hydrateFromLocalStorage();
  }, [hydrateFromLocalStorage]);

  return { hydrateFromLocalStorage };
}

export function registerMenuLookupsFromItems(
  dispatch: ReturnType<typeof useAppDispatch>,
  items: ItemType[]
) {
  if (items.length > 0) {
    dispatch(mergeMenuLookupsFromItems(items));
  }
}
